import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TVShowCard from "@/components/base/TVShowCard";
import { tvShows } from "@/mocks/data";

const categoryNames: Record<string, string> = {
  xuanyi: "悬疑",
  dongzuo: "动作",
  aiqing: "爱情",
  kehuan: "科幻",
  xiju: "喜剧",
  kongbu: "恐怖",
};

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const show = tvShows.find((s) => s.id === Number(id));

  if (!show) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="mx-auto px-4 md:px-6 text-center py-16">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的剧集不存在或已下架。</p>
            <Link
              to="/"
              className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedShows = tvShows
    .filter((s) => s.category === show.category && s.id !== show.id)
    .slice(0, 3);

  const categoryLabel = categoryNames[show.category] || show.category;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${show.title} - ${show.titleEn} | 红桃影视大全`,
    description: show.description,
    url: `https://www.hongtao-tv.com/article/${show.id}`,
    author: {
      "@type": "Organization",
      name: "红桃影视大全",
    },
    publisher: {
      "@type": "Organization",
      name: "红桃影视大全",
    },
    datePublished: `${show.year}-01-01`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.hongtao-tv.com/article/${show.id}`,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `${show.title}在哪个平台可以免费在线观看？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `《${show.title}》可以在红桃影视大全免费在线观看，提供1080P超清画质和中英双语字幕，无需注册即可播放。`,
        },
      },
      {
        "@type": "Question",
        name: `${show.title}一共有多少集？目前更新到第几集了？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `《${show.title}》共有${show.episodes}集，目前${show.status === "连载中" ? "仍在更新中" : "已全部完结"}。`,
        },
      },
      {
        "@type": "Question",
        name: `${show.title}的主演阵容有哪些？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `《${show.title}》由${show.actors.join("、")}等实力派演员主演。`,
        },
      },
      {
        "@type": "Question",
        name: `${show.title}是什么类型的欧美剧？剧情简介是什么？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `《${show.title}》是一部${categoryLabel}类欧美剧。${show.description}`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="mx-auto px-4 md:px-6 max-w-4xl">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: `${categoryLabel}欧美剧`, path: `/category/${show.category}` },
                { label: show.title },
              ]}
            />

            <article className="mt-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {categoryLabel}
                </span>
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {show.year}
                </span>
                <span className="flex items-center gap-1 text-accent-500 text-sm font-bold">
                  <i className="ri-star-fill"></i>
                  {show.rating}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  show.status === "连载中"
                    ? "bg-secondary-100 text-secondary-700"
                    : "bg-background-200 text-foreground-600"
                }`}>
                  {show.status}
                </span>
              </div>

              <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-2">
                {show.title}
              </h1>
              <p className="text-foreground-500 text-sm mb-6">{show.titleEn}</p>

              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={show.image}
                  alt={`${show.title} - ${show.titleEn}`}
                  title={`红桃影视大全 - ${show.title} 高清欧美剧`}
                  className="w-full object-cover object-top aspect-video"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <section className="mb-8">
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {show.description}
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情亮点</h2>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      本剧以精湛的剧本和出色的表演赢得了观众和评论家的一致好评。{show.actors.slice(0, 2).join("和")}的精彩演绎为本剧注入了强烈的戏剧张力。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed mt-3">
                      制作团队汇集了好莱坞顶级人才，从摄影、剪辑到配乐，每一个环节都精益求精。{show.episodes}集的剧集节奏把控恰到好处，既有扣人心弦的主线剧情，又有丰富多彩的支线故事。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed mt-3">
                      画面质感方面，本剧采用4K超高清拍摄，每一帧都可以截图当壁纸。1080P版本已经呈现出令人惊叹的画质表现，配合中英双语字幕，让国内观众可以无障碍欣赏这部佳作。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed mt-3">
                      目前本剧{show.status}，是红桃影视大全平台最受欢迎的欧美剧之一。
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">常见问题</h2>
                    <div className="space-y-3">
                      {faqSchema.mainEntity.map((item, i) => (
                        <details key={i} className="group bg-background-50 border border-background-200/70 rounded-lg">
                          <summary className="px-4 py-3 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-800">
                            {item.name}
                            <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                          </summary>
                          <div className="px-4 pb-3 text-sm text-foreground-600 leading-relaxed">
                            {item.acceptedAnswer.text}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                </div>

                <aside>
                  <div className="bg-background-100 rounded-lg p-5 mb-6">
                    <h3 className="font-heading font-bold text-foreground-900 text-sm mb-4">剧集信息</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">主演</dt>
                        <dd className="text-foreground-800 text-right">{show.actors.join(" / ")}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">集数</dt>
                        <dd className="text-foreground-800">{show.episodes}集</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">年份</dt>
                        <dd className="text-foreground-800">{show.year}年</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">类型</dt>
                        <dd className="text-foreground-800">{categoryLabel}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">状态</dt>
                        <dd className="text-foreground-800">{show.status}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">评分</dt>
                        <dd className="text-accent-500 font-bold">{show.rating}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="bg-primary-50 rounded-lg p-5">
                    <h3 className="font-heading font-bold text-primary-800 text-sm mb-2">立即观看</h3>
                    <p className="text-xs text-primary-600 mb-4">1080P超清画质 · 中英双语字幕 · 免费在线观看</p>
                    <Link
                      to={`/category/${show.category}`}
                      className="block w-full text-center px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      查看更多{categoryLabel}剧
                    </Link>
                  </div>
                </aside>
              </div>
            </article>

            {relatedShows.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-5">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-product-shop>
                  {relatedShows.map((s) => (
                    <TVShowCard key={s.id} show={s} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}